/**
 * Loading and layering.
 *
 * Precedence, highest first:
 *   1. CLI flag        2. environment      3. project config
 *   4. user config     5. built-in default
 *
 * ⚠️ ARRAYS REPLACE, THEY DO NOT CONCATENATE. Concatenation is the more convenient behaviour and
 * the more surprising one: a user who trims a list and gets the original back plus their trim has
 * no way to remove a default.
 *
 * ⛔ Dimensions merge by `id`, never by position, and there is no delete — set `enabled = false`.
 * A config that can make a dimension vanish silently is one where nobody can tell whether Security
 * actually ran.
 *
 * docs/spec/config.md §2
 */

import { readFileSync, existsSync } from "node:fs"
import { homedir } from "node:os"
import { join, isAbsolute, dirname } from "node:path"
import { parse as parseToml } from "smol-toml"
import {
  DEFAULTS,
  type SibylConfig,
  type DimensionConfig,
  type EffortLevel,
  type BackendSelector,
  DIMENSION_DEFAULTS,
} from "./schema.js"
import { validate, type Diagnostic, ConfigError, EXIT, nearestKey } from "./validate.js"

/** Where a value came from, so `sibyl config print` can annotate it. */
export type Layer = "default" | "user" | "project" | "env" | "flag"

export interface LoadedConfig {
  readonly config: SibylConfig
  readonly provenance: ReadonlyMap<string, Layer>
  readonly warnings: readonly Diagnostic[]
  readonly files: readonly string[]
}

export function expandPath(p: string): string {
  let out = p.replace(/\$\{([A-Z_]+):-([^}]*)\}/g, (_, name: string, fallback: string) => {
    const v = process.env[name]
    return v !== undefined && v !== "" ? v : fallback
  })
  out = out.replace(/\$\{([A-Z_]+)\}/g, (_, name: string) => process.env[name] ?? "")
  if (out === "~") return homedir()
  if (out.startsWith("~/")) return join(homedir(), out.slice(2))
  return out
}

/** TOML's snake_case to our camelCase, done once at the boundary. */
function camel(k: string): string {
  return k.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase())
}

const KNOWN_TOP_LEVEL = [
  "schema_version",
  "server",
  "backend",
  "stage",
  "dimension",
  "review",
  "severity",
  "suggestion",
  "paths",
  "diff",
  "concurrency",
  "cache",
  "triage",
  "comments",
  "post",
  "repo",
  "repo_policy",
  "secrets",
  "validation",
  "log",
]

type Plain = Record<string, unknown>

function isPlainObject(v: unknown): v is Plain {
  return typeof v === "object" && v !== null && !Array.isArray(v)
}

/**
 * Deep-merge one layer over a base.
 *
 * Objects merge per key. Arrays replace wholesale. Records the layer for every leaf it sets.
 */
function mergeLayer(
  base: Plain,
  over: Plain,
  layer: Layer,
  provenance: Map<string, Layer>,
  path = "",
): Plain {
  const out: Plain = { ...base }
  for (const [rawKey, value] of Object.entries(over)) {
    const key = camel(rawKey)
    const dotted = path === "" ? key : `${path}.${key}`
    const existing = out[key]
    if (isPlainObject(value) && isPlainObject(existing)) {
      out[key] = mergeLayer(existing, value, layer, provenance, dotted)
    } else if (Array.isArray(value)) {
      // ⛔ AN ARRAY OF TABLES STILL NEEDS ITS KEYS CAMELISED. Measured 2026-09-15: an
      // `[[paths.overrides]]` block carrying `severity_floor = "high"` was assigned wholesale, so
      // the resolved config held `severity_floor`, nothing read it, and NO warning fired — the user
      // had set a severity floor and silently got nothing. That is the same defect as a silently
      // ignored flag, which this loader refuses everywhere else. `[[dimension]]` escaped it only
      // because `mergeDimensions` happens to call back into this function per block.
      //
      // ⚠️ Each element is camelised on its own rather than merged against a positional counterpart.
      // An array REPLACES, so there is nothing to merge with, and matching by index would silently
      // inherit fields from an unrelated entry that happened to sit at the same position.
      out[key] = value.map((el) => (isPlainObject(el) ? mergeLayer({}, el, layer, provenance, dotted) : el))
      provenance.set(dotted, layer)
    } else {
      out[key] = value
      provenance.set(dotted, layer)
    }
  }
  return out
}

/** Dimensions merge by id. A new id appends; a known id overrides that block's named keys only. */
function mergeDimensions(
  base: DimensionConfig[],
  incoming: Plain[],
  layer: Layer,
  provenance: Map<string, Layer>,
): DimensionConfig[] {
  const byId = new Map(base.map((d) => [d.id, { ...d }]))
  const order = base.map((d) => d.id)

  // ⛔ Duplicate ids must be caught HERE, not in validate(). By the time validation runs the two
  // blocks have already merged into one and the duplicate is invisible — which is precisely the
  // silent last-wins behaviour the check exists to prevent. Note that the same id appearing in a
  // DIFFERENT layer is legitimate override; only a repeat within one file is the mistake.
  const seenThisLayer = new Set<string>()
  for (const raw of incoming) {
    const dupId = raw["id"]
    if (typeof dupId === "string") {
      if (seenThisLayer.has(dupId)) {
        throw new ConfigError([
          {
            level: "error",
            message: `duplicate dimension id ${JSON.stringify(dupId)} in the same config file`,
            hint: "Two [[dimension]] blocks with one id merge silently into one. If you meant two dimensions, give them different ids.",
          },
        ])
      }
      seenThisLayer.add(dupId)
    }
  }

  for (const raw of incoming) {
    const id = raw["id"]
    if (typeof id !== "string") {
      throw new ConfigError([
        { level: "error", message: "a [[dimension]] block has no `id`", hint: "The id is required and must be unique." },
      ])
    }
    const prior = byId.get(id)
    // A new id starts from the dimension defaults, never from {} — see DIMENSION_DEFAULTS.
    const base = (prior ?? { id, ...DIMENSION_DEFAULTS }) as unknown as Plain
    const merged = mergeLayer(base, raw, layer, provenance, `dimension.${id}`)
    byId.set(id, merged as unknown as DimensionConfig)
    if (prior === undefined) order.push(id)
  }
  return order.map((id) => byId.get(id) as DimensionConfig)
}

function readTomlFile(path: string): { data: Plain; text: string } {
  const text = readFileSync(path, "utf8")
  try {
    return { data: parseToml(text) as Plain, text }
  } catch (e) {
    const msg = (e as Error).message
    throw new ConfigError([
      { level: "error", message: `could not parse ${path}: ${msg}`, where: path },
    ])
  }
}

/** Reject a key the schema does not know, with a nearest-match suggestion. */
function checkUnknownTopLevel(data: Plain, file: string, mode: "error" | "warn" | "ignore"): Diagnostic[] {
  if (mode === "ignore") return []
  const out: Diagnostic[] = []
  for (const key of Object.keys(data)) {
    if (KNOWN_TOP_LEVEL.includes(key)) continue
    const near = nearestKey(key, KNOWN_TOP_LEVEL)
    out.push({
      level: mode,
      message: `unknown key \`${key}\`${near !== undefined ? ` — did you mean \`${near}\`?` : ""}`,
      where: file,
      hint: "A silently ignored key is a setting you believe is in effect. Set validation.unknown_keys = \"warn\" to downgrade this.",
    })
  }
  return out
}

/** `SIBYL_BACKEND_CLAUDE_CLI_MODEL` → `backend.claudeCli.model`. Scalars only. */
function envOverlay(provenance: Map<string, Layer>): Plain {
  const out: Plain = {}
  for (const [name, value] of Object.entries(process.env)) {
    if (!name.startsWith("SIBYL_") || value === undefined) continue
    const segments = name.slice("SIBYL_".length).toLowerCase().split("_")
    // Longest-prefix match against the real object graph, so `claude_cli` resolves as one segment.
    let node: Plain = out
    let cursor: unknown = DEFAULTS as unknown as Plain
    let i = 0
    const dotted: string[] = []
    while (i < segments.length) {
      let matched: string | undefined
      for (let take = segments.length - i; take >= 1; take--) {
        const candidate = camel(segments.slice(i, i + take).join("_"))
        if (isPlainObject(cursor) && candidate in cursor) {
          matched = candidate
          i += take
          break
        }
      }
      if (matched === undefined) break
      dotted.push(matched)
      const next = (cursor as Plain)[matched]
      if (i >= segments.length) {
        const parsed: unknown =
          value === "true" ? true : value === "false" ? false : /^-?\d+(\.\d+)?$/.test(value) ? Number(value) : value
        node[matched] = Array.isArray(next) ? value.split(",") : parsed
        provenance.set(dotted.join("."), "env")
      } else {
        node[matched] = (node[matched] as Plain) ?? {}
        node = node[matched] as Plain
        cursor = next
      }
    }
  }
  return out
}

export interface LoadOptions {
  /** Replaces the project layer entirely. */
  readonly configPath?: string
  readonly noUserConfig?: boolean
  /** Drops both file layers — built-in defaults plus env and flags only. */
  readonly defaultsOnly?: boolean
  /** Already-camelCased partial, highest precedence. */
  readonly flags?: Plain
  readonly cwd?: string
}

export function loadConfig(opts: LoadOptions = {}): LoadedConfig {
  const cwd = opts.cwd ?? process.cwd()
  const provenance = new Map<string, Layer>()
  const files: string[] = []
  const warnings: Diagnostic[] = []

  let merged: Plain = structuredClone(DEFAULTS) as unknown as Plain
  let dimensions: DimensionConfig[] = structuredClone(DEFAULTS.dimensions)
  let lastText: string | undefined

  const layerFiles: Array<{ path: string; layer: Layer }> = []
  if (!opts.defaultsOnly) {
    if (opts.noUserConfig !== true) {
      const userPath = join(expandPath(DEFAULTS.secrets.store.replace(/\/credentials\.env$/, "")), "sibyl.toml")
      if (existsSync(userPath)) layerFiles.push({ path: userPath, layer: "user" })
    }
    const projectPath =
      opts.configPath !== undefined
        ? isAbsolute(opts.configPath)
          ? opts.configPath
          : join(cwd, opts.configPath)
        : join(cwd, ".sibyl", "sibyl.toml")
    if (existsSync(projectPath)) layerFiles.push({ path: projectPath, layer: "project" })
    else if (opts.configPath !== undefined) {
      throw new ConfigError(
        [{ level: "error", message: `config file not found: ${projectPath}` }],
        EXIT.USAGE,
      )
    }
  }

  for (const { path, layer } of layerFiles) {
    const { data, text } = readTomlFile(path)
    lastText = text
    files.push(path)
    warnings.push(...checkUnknownTopLevel(data, path, DEFAULTS.validation.unknownKeys))
    const dimBlocks = data["dimension"]
    delete data["dimension"]
    merged = mergeLayer(merged, data, layer, provenance)
    if (Array.isArray(dimBlocks)) {
      dimensions = mergeDimensions(dimensions, dimBlocks as Plain[], layer, provenance)
    }
  }

  merged = mergeLayer(merged, envOverlay(provenance), "env", provenance)
  if (opts.flags !== undefined) merged = mergeLayer(merged, opts.flags, "flag", provenance)

  merged["dimensions"] = dimensions
  const config = merged as unknown as SibylConfig

  // Resolve prompt paths relative to the file that named them, before they are checked.
  const baseDir = files.length > 0 ? dirname(files[files.length - 1] as string) : cwd
  for (const d of config.dimensions) {
    if (d.promptFile !== undefined && d.promptFile !== "" && !isAbsolute(d.promptFile)) {
      d.promptFile = join(baseDir, d.promptFile)
    }
  }

  const hardErrors = warnings.filter((w) => w.level === "error")
  if (hardErrors.length > 0) throw new ConfigError(warnings)

  warnings.push(...validate(config, lastText))
  return { config, provenance, warnings, files }
}

/** Human-readable resolution, with the layer that set each non-default value. */
export function describeResolution(loaded: LoadedConfig): string {
  const lines: string[] = []
  lines.push(`config files read: ${loaded.files.length === 0 ? "(none — built-in defaults)" : loaded.files.join(", ")}`)
  const nonDefault = [...loaded.provenance.entries()].filter(([, l]) => l !== "default")
  if (nonDefault.length === 0) lines.push("every value is at its built-in default")
  else {
    lines.push(`${nonDefault.length} value(s) differ from the default:`)
    for (const [key, layer] of nonDefault) lines.push(`  ${key}  ← ${layer}`)
  }
  return lines.join("\n")
}

export type { SibylConfig, DimensionConfig, EffortLevel, BackendSelector }
